import { event } from "@foretagsplatsen/yaem";
import AlertCollector from "../../../alerts/AlertCollector.js";
import ArrayStream from "../../../list/streams/ArrayStream.js";

/**
 * Abstract strategy to render a WidgetsListWidget.
 * This class is modeled around the idea that we use a sync ArrayStream to
 * handle the data as an array. Yet it handles object model for data
 * management (overriding `_getItemsOrEmpty`) and keeps the door open for the
 * oDataStream use case (overriding `getListSourceStream`).
 *
 * Terminology:
 *     - item: one individual business value
 *     - items: list of all business values
 *     - entry: widget displaying one item
 *
 * @abstract
 * @param {*[]} [items] - List of items to render.
 *   In the case of an oDataSource, it can be ignored
 */
export default class AbstractWidgetsListStrategy {
	constructor({ items } = {}) {
		this._items = items;
		this._onChange = event();
	}

	/**
	 * Register a callback when the strategy updates itself its items.
	 * This should be only used for the communication with the widget.
	 *
	 * If as an external user, you want to have some events when the items
	 * change, you should create another event, and manage it by hand.
	 *
	 * @param {function} callback - Function invoked when an entry is added
	 *   or removed
	 */
	onChange(callback) {
		this._onChange.register(callback);
	}

	getListSourceStream() {
		return new ArrayStream({ data: this._getItemsOrEmpty() });
	}

	/**
	 * When returning true, the items are rendered inlined, instead of one line
	 * after another.
	 *
	 * @return {boolean}
	 */
	isInlined() {
		return false;
	}

	/**
	 * Provide optional css classes to apply to the top level node
	 * of the widget.
	 *
	 * @return {string}
	 */
	extraCSSClasses() {
		return "";
	}

	/**
	 * Returns true when the validation succeeds, false otherwise.
	 *
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 * @return {boolean}
	 */
	isValid(context) {
		return !this.validation(context).hasError();
	}

	/**
	 * Validate the whole data, then validate each item individually.
	 * It should throw an exception if not valid.
	 *
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 * @return {AlertCollector}
	 */
	validation(context) {
		const collector = new AlertCollector();

		this._validate({ collector, context });

		for (const item of this._getItemsOrEmpty()) {
			this._validateItem({ item, collector, context });
		}

		return collector;
	}

	/**
	 * Return true when a new item can be added.
	 *
	 * @return {boolean}
	 */
	canAddItem() {
		return true;
	}

	/**
	 * Return true when the provided item can be deleted.
	 * By default, doesn't allow to remove the last item.
	 *
	 * @param {*} item - Item to test
	 * @param {number} index - Item's index
	 * @return {boolean}
	 */
	canDeleteItem({ item: _item, index: _index }) {
		return this._getItemsOrEmpty().length > 1;
	}

	/**
	 * Create and append a new entry to the list of items.
	 */
	addNewItem() {
		const item = this._makeNewItem();
		this._alterItems((items) => items.push(item));
	}

	/**
	 * Remove the provided item.
	 *
	 * @param {*} item - Item to remove
	 * @param {number} index - Item's index
	 */
	deleteItem({ item: _item, index }) {
		this._alterItems((items) => {
			items.splice(index, 1);
		});
	}

	/**
	 * Define how to render a business item. This method can also be used to
	 * keep track of models to validate.
	 *
	 * @abstract
	 * @param {*} item - Item to test
	 * @param {number} index - Item's index
	 */
	itemRenderer({ item: _item, index: _index }) {
		throw new Error("Subclass responsibility");
	}

	/**
	 * Function returning a Renderable that display an add button.
	 * The `onClick` behavior is provided by the WidgetsListWidget and must
	 * be used.
	 *
	 * @abstract
	 * @param {function} onClick - Function to invoke when the button is clicked
	 */
	addButtonRenderer({ onClick: _onClick }) {
		throw new Error("Subclass responsibility");
	}

	/**
	 * Function returning a Renderable that display a delete button.
	 * The `onClick` behavior is provided by the WidgetsListWidget and must
	 * be used.
	 *
	 * @abstract
	 * @param {*} item - Item to test
	 * @param {function} onClick - Function to invoke when the button is clicked
	 */
	deleteButtonRenderer({ item: _item, onClick: _onClick }) {
		throw new Error("Subclass responsibility");
	}

	/**
	 * When false, the WidgetsListWidget will render the validation result itself.
	 * If true, the rendering of the validation result is delegated to an external widget.
	 *
	 * @return {boolean}
	 */
	shouldExternalizeValidationRendering() {
		return false;
	}

	/**
	 * When true, the widget will validate itself on entry addition/deletion.
	 *
	 * @return {boolean}
	 */
	isValidatingOnChange() {
		return false;
	}

	/**
	 * Returns a new item to add to the widget's items.
	 *
	 * @abstract
	 * @return {*} - New item instance
	 */
	_makeNewItem() {
		throw new Error("Subclass responsibility");
	}

	/**
	 * Validate the items as a whole. For individual item validation, see
	 * `_validateItem`.
	 *
	 * @param {AlertCollector} collector - Collect all the errors
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 */
	_validate({ collector: _collector, context: _context }) {}

	/**
	 * Validate the provided item. In case of an invalid item, it should
	 * populate the collector.
	 *
	 * @param {*} item - Item to validate
	 * @param {AlertCollector} collector - Collect all the errors
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 */
	_validateItem({ item: _item, collector: _collector, context: _context }) {}

	/**
	 * Mutate the local state if it exists.
	 *
	 * @param {function(object[]): void} fn - Function invoke to alter the
	 *   list of items in place
	 */
	_alterItems(fn) {
		if (!this._items) return;

		fn(this._items);
	}

	/**
	 * Get the list of items to render, or an empty list by default.
	 *
	 * @return {*[]}
	 */
	_getItemsOrEmpty() {
		return this._items ?? [];
	}

	_triggerOnChange() {
		this._onChange.trigger();
	}
}
